import * as React from 'react';
import ClassDetailsForm from "./ClassDetailsForm";
import Grid from "@material-ui/core/Grid";
import {css, StyleSheet} from "aphrodite";
import ReactPlayer from "react-player";
import * as CopyToClipboard from "react-copy-to-clipboard";
import Alert from "react-s-alert";
import * as firebase from "firebase";
import {LinearProgress} from "@material-ui/core";
import {isInvalidTimeString} from "../../../helpers/Validation";

interface ClassDetailsProps {
    currentClassId: string | null;
    onClassSaved: (classId: string) => void;
}

interface ClassDetailsState {
    classData: any; // TODO: define classType
    errors: any;
    isLoading: boolean;
}

const ALERT_PROPS = {
    effect: 'slide',
    position: 'top',
    timeout: 5000
};

export default class ClassDetails extends React.Component<ClassDetailsProps, ClassDetailsState> {

    private reactPlayerRef = React.createRef<ReactPlayer>();

    private copyButtonRef = React.createRef<HTMLButtonElement>();

    private isInvalid = {
        replayStartTime: isInvalidTimeString,
        replayEndTime: isInvalidTimeString
    };

    constructor(props: ClassDetailsProps) {
        super(props);
        this.state = {
            classData: null,
            errors: {},
            isLoading: false
        };
    }

    public componentDidUpdate(prevProps: Readonly<ClassDetailsProps>, prevState: Readonly<ClassDetailsState>)  {
        if(prevProps.currentClassId !== this.props.currentClassId) {
            this.fetchCurrentClassData();
        }
    }

    public componentDidMount() {
        this.fetchCurrentClassData();
    }

    public render() {
        return(
            <div>
                {
                    this.state.isLoading && <LinearProgress className={css(styles.loading)} />
                }
                {
                    this.state.classData && !this.state.isLoading &&
                    <Grid container className={css(styles.grid)}>
                        <Grid item xs={7}>
                            <div className="videoBlock">
                                <ReactPlayer
                                    ref={this.reactPlayerRef}
                                    className="player"
                                    url={this.getCurrentStreamUrl()}
                                    playing={true}
                                    controls={true}
                                    onError={this.onError}
                                />
                                <CopyToClipboard
                                    text={this.getCurrentStreamUrl()}
                                    onCopy={() => Alert.success('Link copied!', ALERT_PROPS)}
                                >
                                    <button
                                        className={"controlButton" + (this.getCurrentStreamUrl() ? "" : " hidden")}
                                        ref={this.copyButtonRef}
                                        hidden={true}>
                                        Copy video Link
                                    </button>
                                </CopyToClipboard>
                            </div>
                        </Grid>
                        <Grid item xs={5}>
                            <ClassDetailsForm
                                onPreviewBeginningPressed={this.onPreviewBeginningPressed}
                                onSave={this.onSave}
                                data={this.state.classData}
                                errors={this.state.errors}
                                onFieldChange={this.onFieldChange}
                                isSaving={this.state.isLoading}
                                onOptimizePressed={this.onOptimizePressed}
                            />
                        </Grid>
                    </Grid>
                }
            </div>
        );
    }

    private getCurrentStreamUrl = () : string => {
        return this.state.classData.replayUrl ? this.state.classData.replayUrl : this.state.classData.playbackURL;
    };

    private onError = (error: any) => {
        console.log(error);
    };

    private onFieldChange = (key: string, data: any, callback?: () => void) => {
        if(this.isInvalid[key]) {
            this.setState(prevState => ({
                    errors: {...prevState.errors, [key]: this.isInvalid[key](data)}
                }
            ));
        }
        this.setState( prevState => ({
            classData: {...prevState.classData, [key]: data}
        }), callback ? callback : () => {return;});
    };

    // Time is entered as m:ss and needs to be converted
    private timeStringToSeconds = (value: string) : number | null => {
        const pieces = value.split(':');
        if(isInvalidTimeString(value)) {
            return null;
        }
        return parseInt(pieces[0], 10)* 60 + parseInt(pieces[1], 10);
    };

    private secondsToTimeString = (time: number) : string => {
        const minutes = Math.floor(time / 60);
        let seconds = (time % 60).toString();
        if(seconds.length === 1) {
            seconds = "0" + seconds;
        }
        return `${minutes}:${seconds}`;
    };

    private prepareForSave = (data: any) => {
        data.replayStartTime = data.replayStartTime ? this.timeStringToSeconds(data.replayStartTime) : null;
        data.replayEndTime = data.replayEndTime ? this.timeStringToSeconds(data.replayEndTime) : null;
        data.dateFirstAired  = data.dateFirstAired ?
            firebase.firestore.Timestamp.fromDate(data.dateFirstAired) : null;
        data.displayedDurationMins = data.displayedDurationMins ? parseInt(data.displayedDurationMins, 10) : null;
        return data;
    };

    private prepareForRender = (data: any) => {
        // certain fields require empty strings or nulls for empty state
        data.replayStartTime = this.exists(data.replayStartTime)
            ? this.secondsToTimeString(data.replayStartTime) : "";
        data.replayEndTime = this.exists(data.replayEndTime)
            ? this.secondsToTimeString(data.replayEndTime) : "";
        data.dateFirstAired = data.dateFirstAired ?
            new Date(data.dateFirstAired.seconds * 1000) : null;
        return data;
    };

    private exists = (data: any) => typeof data !== 'undefined' && data !== null;

    private onSave = () => {
        const classId = this.props.currentClassId;
        if(classId && this.state.classData) {
            this.setState({isLoading: true});
            firebase.firestore().collection('classes')
                .doc(classId).update(this.prepareForSave({...this.state.classData}))
                .then(() => {
                    this.setState({isLoading: false});
                    this.props.onClassSaved(classId);
                    Alert.success('Saved', ALERT_PROPS);
                })
                .catch(() => {
                    this.setState({isLoading: false});
                    Alert.error('Problems saving data', ALERT_PROPS);
                });
        }
    };

    private onPreviewBeginningPressed = () => {
        const seekTo = this.state.classData.replayStartTime ? this.timeStringToSeconds(this.state.classData.replayStartTime) : null;
        if(seekTo && this.reactPlayerRef.current) {
            this.reactPlayerRef.current.seekTo(seekTo, "seconds");
        }
    };

    private onOptimizePressed = () => {
        // If we are using mux, skip the manifest optimization step
        if (this.state.classData.playbackURL && this.state.classData.playbackURL.indexOf('mux.com') >= 0) {
            this.onFieldChange('replayUrl', this.state.classData.playbackURL, this.onSave);
        } else {
            this.setState({isLoading: true}, () => {
                const optimizeRecordedClassManifest = firebase.functions().httpsCallable('optimizeRecordedClassManifest');

                optimizeRecordedClassManifest({url: this.state.classData.playbackURL})
                    .then( response => {
                        this.onFieldChange('replayUrl', response.data, this.onSave);
                    })
                    .catch( () => {
                        Alert.error('Unable to generate optimized file');
                        this.setState({isLoading: false});
                    });
            });
        }
    };

    private fetchCurrentClassData = () => {
        if(this.props.currentClassId) {
            this.setState({
               isLoading: true,
               classData: null,
               errors: {}
            });
            firebase.firestore().collection('classes')
                .doc(this.props.currentClassId).get()
                .then(o => {
                    this.setState({
                        isLoading: false,
                        classData: this.prepareForRender(o.data()),
                        errors: {}
                    });

                });
        }
    };
};

const styles = StyleSheet.create({
    grid: {
        width: '90vw',
        paddingLeft: '5vw',
        paddingRight: '5vw',
        boxSizing: 'content-box'
    },
    loading: {
        marginBottom: '20px'
    }
});


// WEBPACK FOOTER //
// ./src/components/replay/classes/ClassDetails.tsx